/**
 * Персистентность pending-попыток для вкладки «Переменные и скидка»:
 * ОДНА запись значения переменной на tenant+admin+userId+definitionId, ОДНА
 * запись персональной скидки на tenant+admin+userId и ОДНА запись создания
 * определения на tenant+admin+key (без фейковой привязки к userId — ключ
 * переменной принадлежит компании, не конкретному клиенту). Переживает
 * unmount/logout/закрытие вкладки — паттерн почти буквально повторяет
 * stateDefinitionAttemptStorage.ts (именованный navigator.locks на
 * claim/clear, маркер = замороженное тело + identity + версия/ключ).
 */
import { isWebLocksAvailable } from '../pages/settings/stateDefinitionAttemptStorage';

export const CLIENT_VAR_ATTEMPT_SCHEMA = 1 as const;

export interface StoredVariableValueBody {
  value: string | number | boolean;
  expected_version: number;
}

export interface StoredDiscountBody {
  personal_discount: number | null;
  expected_version: number;
}

export type StoredVariableValueType = 'string' | 'int' | 'bool' | 'date' | 'enum';

export interface StoredDefinitionCreateBody {
  key: string;
  label: string;
  value_type: StoredVariableValueType;
  enum_choices: string[] | null;
}

export interface StoredVariableValueMarker {
  schema: typeof CLIENT_VAR_ATTEMPT_SCHEMA;
  kind: 'value';
  tenantId: number;
  adminId: number;
  userId: number;
  definitionId: number;
  expectedVersion: number;
  bodyJson: string;
}

export interface StoredDiscountMarker {
  schema: typeof CLIENT_VAR_ATTEMPT_SCHEMA;
  kind: 'discount';
  tenantId: number;
  adminId: number;
  userId: number;
  expectedVersion: number;
  bodyJson: string;
}

export interface StoredDefinitionCreateMarker {
  schema: typeof CLIENT_VAR_ATTEMPT_SCHEMA;
  kind: 'definition_create';
  tenantId: number;
  adminId: number;
  key: string;
  bodyJson: string;
}

export type StoredRead<M> =
  | { status: 'empty' }
  | { status: 'valid'; marker: M }
  | { status: 'corrupt' }
  | { status: 'storage_error' };

const DEF_KEY_RE = /^[a-z][a-z0-9_]{0,63}$/;
const VALUE_TYPES = new Set<StoredVariableValueType>(['string', 'int', 'bool', 'date', 'enum']);
const MAX_BODY_JSON_CHARS = 65536;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonNegativeSafeInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

function isTypedScalar(value: unknown): value is string | number | boolean {
  if (typeof value === 'string' || typeof value === 'boolean') return true;
  return typeof value === 'number' && Number.isFinite(value);
}

function isValidVariableValueBody(body: unknown): body is StoredVariableValueBody {
  if (!isPlainObject(body)) return false;
  const keys = Object.keys(body);
  if (keys.length !== 2 || !('value' in body) || !('expected_version' in body)) return false;
  if (!isTypedScalar(body.value)) return false;
  return isNonNegativeSafeInt(body.expected_version);
}

function isValidDiscountBody(body: unknown): body is StoredDiscountBody {
  if (!isPlainObject(body)) return false;
  const keys = Object.keys(body);
  if (keys.length !== 2 || !('personal_discount' in body) || !('expected_version' in body)) return false;
  const discount = body.personal_discount;
  if (discount !== null) {
    if (typeof discount !== 'number') return false;
    if (!(Number.isSafeInteger(discount) && discount >= 0 && discount <= 100)) return false;
  }
  return isNonNegativeSafeInt(body.expected_version);
}

function isValidDefinitionCreateBody(body: unknown): body is StoredDefinitionCreateBody {
  if (!isPlainObject(body)) return false;
  if (Object.keys(body).length !== 4) return false;
  if (typeof body.key !== 'string' || !DEF_KEY_RE.test(body.key)) return false;
  if (typeof body.label !== 'string' || body.label.length < 1 || body.label.length > 255) return false;
  if (typeof body.value_type !== 'string' || !VALUE_TYPES.has(body.value_type as StoredVariableValueType)) return false;
  const choices = body.enum_choices;
  if (choices !== null) {
    if (!Array.isArray(choices) || choices.length === 0) return false;
    if (!choices.every((c) => typeof c === 'string' && c.length > 0)) return false;
  }
  if (body.value_type === 'enum' && choices === null) return false;
  if (body.value_type !== 'enum' && choices !== null) return false;
  return true;
}

export function valueLockName(tenantId: number, adminId: number, userId: number, definitionId: number): string {
  return `client-var-value:${tenantId}:${adminId}:${userId}:${definitionId}`;
}
export function valueStorageKey(tenantId: number, adminId: number, userId: number, definitionId: number): string {
  return `client_var_value:${tenantId}:${adminId}:${userId}:${definitionId}`;
}
export function discountLockName(tenantId: number, adminId: number, userId: number): string {
  return `client-var-discount:${tenantId}:${adminId}:${userId}`;
}
export function discountStorageKey(tenantId: number, adminId: number, userId: number): string {
  return `client_var_discount:${tenantId}:${adminId}:${userId}`;
}
export function definitionCreateLockName(tenantId: number, adminId: number, key: string): string {
  return `client-var-def-create:${tenantId}:${adminId}:${key}`;
}
export function definitionCreateStorageKey(tenantId: number, adminId: number, key: string): string {
  return `client_var_def_create:${tenantId}:${adminId}:${key}`;
}

function isValidValueMarkerShape(
  value: unknown, tenantId: number, adminId: number, userId: number, definitionId: number,
): value is StoredVariableValueMarker {
  if (!isPlainObject(value)) return false;
  const r = value as Record<string, unknown>;
  if (Object.keys(r).length !== 8) return false;
  if (r.schema !== CLIENT_VAR_ATTEMPT_SCHEMA || r.kind !== 'value') return false;
  if (r.tenantId !== tenantId || r.adminId !== adminId || r.userId !== userId || r.definitionId !== definitionId) return false;
  if (!isNonNegativeSafeInt(r.expectedVersion)) return false;
  if (typeof r.bodyJson !== 'string' || r.bodyJson.length === 0 || r.bodyJson.length > MAX_BODY_JSON_CHARS) return false;
  let parsed: unknown;
  try { parsed = JSON.parse(r.bodyJson); } catch { return false; }
  if (!isValidVariableValueBody(parsed)) return false;
  return (parsed as StoredVariableValueBody).expected_version === r.expectedVersion;
}

function isValidDiscountMarkerShape(
  value: unknown, tenantId: number, adminId: number, userId: number,
): value is StoredDiscountMarker {
  if (!isPlainObject(value)) return false;
  const r = value as Record<string, unknown>;
  if (Object.keys(r).length !== 7) return false;
  if (r.schema !== CLIENT_VAR_ATTEMPT_SCHEMA || r.kind !== 'discount') return false;
  if (r.tenantId !== tenantId || r.adminId !== adminId || r.userId !== userId) return false;
  if (!isNonNegativeSafeInt(r.expectedVersion)) return false;
  if (typeof r.bodyJson !== 'string' || r.bodyJson.length === 0 || r.bodyJson.length > MAX_BODY_JSON_CHARS) return false;
  let parsed: unknown;
  try { parsed = JSON.parse(r.bodyJson); } catch { return false; }
  if (!isValidDiscountBody(parsed)) return false;
  return (parsed as StoredDiscountBody).expected_version === r.expectedVersion;
}

function isValidDefinitionCreateMarkerShape(
  value: unknown, tenantId: number, adminId: number, key: string,
): value is StoredDefinitionCreateMarker {
  if (!isPlainObject(value)) return false;
  const r = value as Record<string, unknown>;
  if (Object.keys(r).length !== 6) return false;
  if (r.schema !== CLIENT_VAR_ATTEMPT_SCHEMA || r.kind !== 'definition_create') return false;
  if (r.tenantId !== tenantId || r.adminId !== adminId || r.key !== key) return false;
  if (typeof r.bodyJson !== 'string' || r.bodyJson.length === 0 || r.bodyJson.length > MAX_BODY_JSON_CHARS) return false;
  let parsed: unknown;
  try { parsed = JSON.parse(r.bodyJson); } catch { return false; }
  return isValidDefinitionCreateBody(parsed);
}

export function readStoredValueAttempt(
  tenantId: number, adminId: number, userId: number, definitionId: number,
): StoredRead<StoredVariableValueMarker> {
  let raw: string | null;
  try { raw = window.localStorage.getItem(valueStorageKey(tenantId, adminId, userId, definitionId)); } catch { return { status: 'storage_error' }; }
  if (raw === null) return { status: 'empty' };
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { return { status: 'corrupt' }; }
  if (!isValidValueMarkerShape(parsed, tenantId, adminId, userId, definitionId)) return { status: 'corrupt' };
  return { status: 'valid', marker: parsed };
}

export function readStoredDiscountAttempt(
  tenantId: number, adminId: number, userId: number,
): StoredRead<StoredDiscountMarker> {
  let raw: string | null;
  try { raw = window.localStorage.getItem(discountStorageKey(tenantId, adminId, userId)); } catch { return { status: 'storage_error' }; }
  if (raw === null) return { status: 'empty' };
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { return { status: 'corrupt' }; }
  if (!isValidDiscountMarkerShape(parsed, tenantId, adminId, userId)) return { status: 'corrupt' };
  return { status: 'valid', marker: parsed };
}

export function readStoredDefinitionCreateAttempt(
  tenantId: number, adminId: number, key: string,
): StoredRead<StoredDefinitionCreateMarker> {
  let raw: string | null;
  try { raw = window.localStorage.getItem(definitionCreateStorageKey(tenantId, adminId, key)); } catch { return { status: 'storage_error' }; }
  if (raw === null) return { status: 'empty' };
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { return { status: 'corrupt' }; }
  if (!isValidDefinitionCreateMarkerShape(parsed, tenantId, adminId, key)) return { status: 'corrupt' };
  return { status: 'valid', marker: parsed };
}

export function writeStoredValueAttempt(marker: StoredVariableValueMarker): boolean {
  try {
    window.localStorage.setItem(valueStorageKey(marker.tenantId, marker.adminId, marker.userId, marker.definitionId), JSON.stringify(marker));
    return true;
  } catch { return false; }
}
export function writeStoredDiscountAttempt(marker: StoredDiscountMarker): boolean {
  try {
    window.localStorage.setItem(discountStorageKey(marker.tenantId, marker.adminId, marker.userId), JSON.stringify(marker));
    return true;
  } catch { return false; }
}
export function writeStoredDefinitionCreateAttempt(marker: StoredDefinitionCreateMarker): boolean {
  try {
    window.localStorage.setItem(definitionCreateStorageKey(marker.tenantId, marker.adminId, marker.key), JSON.stringify(marker));
    return true;
  } catch { return false; }
}

export function removeStoredValueAttempt(tenantId: number, adminId: number, userId: number, definitionId: number): boolean {
  try { window.localStorage.removeItem(valueStorageKey(tenantId, adminId, userId, definitionId)); return true; } catch { return false; }
}
export function removeStoredDiscountAttempt(tenantId: number, adminId: number, userId: number): boolean {
  try { window.localStorage.removeItem(discountStorageKey(tenantId, adminId, userId)); return true; } catch { return false; }
}
export function removeStoredDefinitionCreateAttempt(tenantId: number, adminId: number, key: string): boolean {
  try { window.localStorage.removeItem(definitionCreateStorageKey(tenantId, adminId, key)); return true; } catch { return false; }
}

export function valueMarkersMatch(a: StoredVariableValueMarker, b: StoredVariableValueMarker): boolean {
  return a.schema === b.schema && a.tenantId === b.tenantId && a.adminId === b.adminId && a.userId === b.userId
    && a.definitionId === b.definitionId && a.expectedVersion === b.expectedVersion && a.bodyJson === b.bodyJson;
}
export function discountMarkersMatch(a: StoredDiscountMarker, b: StoredDiscountMarker): boolean {
  return a.schema === b.schema && a.tenantId === b.tenantId && a.adminId === b.adminId && a.userId === b.userId
    && a.expectedVersion === b.expectedVersion && a.bodyJson === b.bodyJson;
}
export function definitionCreateMarkersMatch(a: StoredDefinitionCreateMarker, b: StoredDefinitionCreateMarker): boolean {
  return a.schema === b.schema && a.tenantId === b.tenantId && a.adminId === b.adminId
    && a.key === b.key && a.bodyJson === b.bodyJson;
}

export function buildValueMarker(
  tenantId: number, adminId: number, userId: number, definitionId: number, expectedVersion: number, bodyJson: string,
): StoredVariableValueMarker {
  return { schema: CLIENT_VAR_ATTEMPT_SCHEMA, kind: 'value', tenantId, adminId, userId, definitionId, expectedVersion, bodyJson };
}
export function buildDiscountMarker(
  tenantId: number, adminId: number, userId: number, expectedVersion: number, bodyJson: string,
): StoredDiscountMarker {
  return { schema: CLIENT_VAR_ATTEMPT_SCHEMA, kind: 'discount', tenantId, adminId, userId, expectedVersion, bodyJson };
}
export function buildDefinitionCreateMarker(
  tenantId: number, adminId: number, key: string, bodyJson: string,
): StoredDefinitionCreateMarker {
  return { schema: CLIENT_VAR_ATTEMPT_SCHEMA, kind: 'definition_create', tenantId, adminId, key, bodyJson };
}

export { isWebLocksAvailable };

export const CLIENT_VAR_ATTEMPT_EVENT = 'client-variable-attempt-changed';

export function notifyClientVarAttemptChanged(detail: Record<string, unknown>): void {
  try { window.dispatchEvent(new CustomEvent(CLIENT_VAR_ATTEMPT_EVENT, { detail })); } catch { /* display-only */ }
}
